import { FC, useEffect, useRef, useState } from "react";
import L, { LatLngExpression } from "leaflet";
import { MapContainer } from "react-leaflet";
import { MapBounds } from "../../../features/results/ResultsOperationalUnits/useOperationalUnitsData";
import { FullscreenMapWrapper } from "./components/FullscreenMapWrapper";
import { MapEvents } from "./components/MapEvents";
import { MapSizeInvalidator } from "./components/MapSizeInvalidator";
import { MapViewUpdater } from "./components/MapViewUpdater";
import { DrawnTileLayer, TileProviderLayer } from "./layers";
import "leaflet/dist/leaflet.css";
import { useCurrentTheme } from "../../../theme/theme";

interface Props {
	isLoading?: boolean;
	tileProvider: "draw" | "heremaps";
	zoom: number;
	minZoom: number;
	maxZoom: number;
	center?: LatLngExpression;
	height?: string | number;
	width?: string;
	bounds?: MapBounds;
	onBoundsChange?: (bounds: MapBounds) => void;
	scrollWheelZoom?: boolean;
	children?: React.ReactNode;
}

/**
 * GraphMap
 * Controlled map component with:
 * - Debounced `onBoundsChange`
 * - Remembered zoom level
 */
const GraphMap: FC<Props> = ({
	isLoading,
	tileProvider,
	zoom,
	minZoom,
	maxZoom,
	center = [45.5, 9.5],
	height = "100%",
	width = "100%",
	bounds,
	onBoundsChange,
	scrollWheelZoom = true,
	children,
}) => {
	const { theme } = useCurrentTheme();
	const mapRef = useRef<L.Map | null>(null);

	/** Ref flags to control when updates are user-driven or programmatic */
	const isProgrammaticMove = useRef(false);
	const lastUserZoomAt = useRef<number | null>(null);

	/** Internal map state */
	const internalMapCenter = useRef<LatLngExpression | null>(null);

	/** Local zoom state — synced to user actions and external updates */
	const fixedMaxZoom = Math.min(maxZoom, 18); // Fixing max zoom to avoid issues with tile providers
	const [currentZoom, setCurrentZoom] = useState(bounds?.zoom ?? zoom);
	const initialZoomRef = useRef(Math.min(Math.max(bounds?.zoom ?? zoom, minZoom), fixedMaxZoom));

	const [isFullscreen, setIsMapFullscreen] = useState<boolean>(false);
	const toggleFullscreen = () => {
		internalMapCenter.current = mapRef.current?.getCenter() ?? null;
		setIsMapFullscreen(!isFullscreen);
	};

	const tileLayer =
		tileProvider === "draw" ? (
			<DrawnTileLayer color="#EAECED" borderColor="#FFFFFF" />
		) : (
			<TileProviderLayer provider={tileProvider} />
		);

	useEffect(() => {
		const t = setTimeout(() => {
			if (mapRef.current) {
				const mapBounds = mapRef.current.getBounds();
				onBoundsChange?.({
					min_lat: mapBounds.getSouth(),
					max_lat: mapBounds.getNorth(),
					min_lon: mapBounds.getWest(),
					max_lon: mapBounds.getEast(),
					zoom: mapRef.current.getZoom(),
				});
			}
		}, 100);
		return () => {
			clearTimeout(t);
		};
	}, [onBoundsChange]);

	return (
		<FullscreenMapWrapper isLoading={isLoading} isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen}>
			<MapContainer
				key={tileProvider || "graph-map"}
				ref={mapRef}
				center={center}
				zoom={initialZoomRef.current}
				minZoom={minZoom}
				maxZoom={fixedMaxZoom}
				style={{
					height,
					width,
					backgroundColor: theme.palette.background.paper,
					borderRadius: "5px",
				}}
				scrollWheelZoom={scrollWheelZoom}
				maxBounds={L.latLngBounds([-140, -240], [140, 240])} // Limit world bounds
			>
				{tileLayer}
				<MapEvents
					isProgrammaticMove={isProgrammaticMove}
					setCurrentZoom={setCurrentZoom}
					lastUserZoomAt={lastUserZoomAt}
					onBoundsChange={onBoundsChange}
				/>
				<MapViewUpdater
					isLoading={isLoading}
					lastUserZoomAt={lastUserZoomAt}
					currentZoom={currentZoom}
					setCurrentZoom={setCurrentZoom}
					isProgrammaticMove={isProgrammaticMove}
					isFullscreen={isFullscreen}
					mapBounds={bounds}
					internalMapCenter={internalMapCenter}
					center={center}
				/>
				<MapSizeInvalidator isLoading={isLoading} isFullscreen={isFullscreen} />
				{children}
			</MapContainer>
		</FullscreenMapWrapper>
	);
};

export default GraphMap;
